import React from 'react';

const JobPosting = () => {
  return (
    <div className="pt-16 px-4 min-h-screen bg-background text-light">
      <div className="max-w-4xl mx-auto py-12">
        <h1 className="text-4xl font-bold text-primary mb-8">Event Coordinator</h1>
        
        {/* Job Introduction */}
        <section className="mb-12">
          <div className="bg-light-gray p-6 rounded-lg shadow-lg">
            <p className="text-xl text-secondary mb-6">
              Join us for a dynamic summer role that will put your event management skills center stage! Rueda de la Vida Dance Society is seeking an enthusiastic and detail-oriented Event Coordinator to help bring our vibrant celebration of dance and culture to life.
            </p>
          </div>
        </section>

        {/* Job Responsibilities */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-primary mb-6">Responsibilities</h2>
          
          {/* Pre-Event Planning Section */}
          <div className="bg-light-gray p-6 rounded-lg shadow-lg mb-6">
            <h3 className="text-xl font-bold text-primary mb-4">1. Pre-Event Planning & Logistics</h3>
            <ul className="list-disc list-inside mb-4 text-secondary">
              <li>Collaborate with the organizing committee to develop event timelines, schedules, and action plans</li>
              <li>Secure necessary permits, licenses, and insurance for the festival</li>
              <li>Identify and coordinate with venue managers, ensuring availability of appropriate spaces for dance floors, stages, food vendors, and participant areas</li>
              <li>Arrange for sound systems, lighting, tents, signage, and any other technical or equipment needs</li>
              <li>Develop and maintain a detailed budget, track expenses, and ensure cost-effective resource allocation</li>
            </ul>
          </div>
          
          {/* Vendor Management Section */}
          <div className="bg-light-gray p-6 rounded-lg shadow-lg mb-6">
            <h3 className="text-xl font-bold text-primary mb-4">2. Vendor & Partner Management</h3>
            <ul className="list-disc list-inside mb-4 text-secondary">
              <li>Identify, recruit, and negotiate with food vendors, merchants, and dance schools that will participate in the festival</li>
              <li>Ensure vendors meet quality, safety, and licensing requirements</li>
              <li>Communicate festival guidelines, set-up schedules, and logistical requirements to all vendors and partners</li>
              <li>Maintain positive relationships and serve as the primary point of contact, addressing any concerns and resolving issues efficiently</li>
            </ul>
          </div>
          
          {/* Entertainment Section */}
          <div className="bg-light-gray p-6 rounded-lg shadow-lg mb-6">
            <h3 className="text-xl font-bold text-primary mb-4">3. Entertainment & Programming Coordination</h3>
            <ul className="list-disc list-inside mb-4 text-secondary">
              <li>Work closely with the artistic team to schedule dance performances, band sets, and DJ lineups</li>
              <li>Arrange rehearsal spaces and times, coordinate performer arrival, setup, and sound checks</li>
              <li>Ensure that all entertainment aligns with the festival's overall vision and appeals to a diverse audience</li>
              <li>Manage any last-minute programming adjustments or stage changes on the day of the event</li>
            </ul>
          </div>
          
          {/* Volunteer Section */}
          <div className="bg-light-gray p-6 rounded-lg shadow-lg mb-6">
            <h3 className="text-xl font-bold text-primary mb-4">4. Volunteer & Staff Oversight</h3>
            <ul className="list-disc list-inside mb-4 text-secondary">
              <li>Recruit, train, and schedule volunteers for various festival roles (e.g., greeting attendees, providing information, assisting with logistics)</li>
              <li>Assign tasks, manage shifts, and ensure that volunteers have the resources they need to succeed</li>
              <li>Provide clear, proactive communication before and during the event, and address any volunteer issues promptly</li>
            </ul>
          </div>
          
          {/* Marketing Section */}
          <div className="bg-light-gray p-6 rounded-lg shadow-lg mb-6">
            <h3 className="text-xl font-bold text-primary mb-4">5. Marketing & Outreach Support</h3>
            <ul className="list-disc list-inside mb-4 text-secondary">
              <li>Collaborate with the marketing and communications team to provide event details, highlight key performers, and share festival updates</li>
              <li>Help distribute promotional materials and assist in community engagement initiatives leading up to the festival</li>
              <li>Liaise with local community groups, sponsors, and partner organizations to maximize visibility and attendance</li>
            </ul>
          </div>
          
          {/* On-Site Management Section */}
          <div className="bg-light-gray p-6 rounded-lg shadow-lg mb-6">
            <h3 className="text-xl font-bold text-primary mb-4">6. On-Site Event Management</h3>
            <ul className="list-disc list-inside mb-4 text-secondary">
              <li>Serve as the main point of contact for all operational aspects of the festival on the day(s) of the event</li>
              <li>Monitor event flow, ensure timely transitions between performances, and troubleshoot technical issues as needed</li>
              <li>Maintain a safe and welcoming atmosphere for attendees, coordinating with security personnel and first-aid providers</li>
              <li>Handle inquiries, manage crowd control, and respond quickly to any emergencies or unexpected challenges</li>
            </ul>
          </div>
          
          {/* Post-Event Section */}
          <div className="bg-light-gray p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-bold text-primary mb-4">7. Post-Event Wrap-Up & Evaluation</h3>
            <ul className="list-disc list-inside mb-4 text-secondary">
              <li>Oversee dismantling of event infrastructure and ensure that the venue is left in a clean, acceptable condition</li>
              <li>Conduct debrief sessions with staff, volunteers, and performers to gather feedback and identify areas for improvement</li>
              <li>Compile attendance data, expense reports, and survey results to produce a detailed event evaluation report</li>
              <li>Provide recommendations for future festivals, highlighting successes and proposing strategies to enhance attendee experience</li>
            </ul>
          </div>
        </section>

        {/* Apply Section */}
        <section className="mb-12">
          <div className="bg-light-gray p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-bold text-primary mb-4">How to Apply</h3>
            <p className="text-secondary mb-6">
              If you're looking for a rewarding summer position that combines leadership opportunity with hands-on event execution, we'd love to hear from you!
            </p>
            <p className="text-secondary text-center">
              To apply, please email your resume and cover letter to <a href="mailto:info@ruedadance.org" className="text-primary hover:underline">info@ruedadance.org</a> with the subject "Event Coordinator Application".
            </p>
          </div>
        </section>

        {/* Additional Information */}
        <section>
          <div className="bg-light-gray p-6 rounded-lg shadow-lg">
            <h3 className="text-xl font-bold text-primary mb-4">Job Information</h3>
            <ul className="space-y-2 text-secondary">
              <li>• Summer seasonal position (May - August)</li>
              <li>• Location: Vancouver, BC</li>
              <li>• Previous event experience preferred</li>
              <li>• Passion for dance and cultural events a plus</li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

export default JobPosting;